import React, { useState, useEffect, useRef, useContext, forwardRef, useImperativeHandle } from "react";
import apiConfig from "../../config/apiConfig";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchForeignResource } from "../../apis/resources";
import { fetchEnum } from "../../apis/enum";
import { useParams } from "react-router-dom";
import { apiFetch } from "../../apis/apiFetch";

import { useRaspStore } from "../../store/raspStore";
import { useGuardianViewModel } from "../../viewModels/useGuardianViewModel";
import { LoginContext } from '../../context/LoginContext';
import './FormStyles.css';
import { toast } from "../../utils/toast";
import { validate } from "../../utils/validator";
import { guardianValidationRules } from "../../validators/applicantValidator";

export type resourceMetaData = {
  resource: string;
  fieldValues: any[];
};

// getCookie removed - using apiFetch

export interface GuardianFormRef {
  save: () => Promise<void>;
  getErrors: () => Record<string, string>;
}

const CreateGuardian = forwardRef<GuardianFormRef>((props, ref) => {
  const { user } = useContext(LoginContext); // logged-in user

  const [resMetaData, setResMetaData] = useState<resourceMetaData[]>([]);
  const [showToast, setShowToast] = useState<any>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const [mobileError, setMobileError] = useState<string>("");
  const [altMobileError, setAltMobileError] = useState<string>("");
  const regex = /^(g_|archived|extra_data)/;
  const apiUrl = apiConfig.getResourceUrl("applications");
  const metadataUrl = apiConfig.getResourceMetaDataUrl("applications");

  const fetchedResources = useRef(new Set<string>());
  const fetchedEnum = useRef(new Set<string>());
  const hasPrePopulated = useRef(false);
  const queryClient = useQueryClient();

  // userId is now managed via LoginContext

  const { appId }: any = useParams<any>();

  const {
    fields,
    setFields,
    enums,
    setEnums,
    foreignKeyData,
    setForeignKeyData,
    dataToSave,
    setDataToSave,
    loadMetadata,
    save,
  } = useGuardianViewModel(user?.userId || "", appId || "");

  useEffect(() => {
    loadMetadata();
  }, []);

  // Auto-set user_id from logged-in user's email
  useEffect(() => {
    if (!user || !user.email_id) return;
    const users = foreignKeyData["Users"] || [];
    if (!users.length) return;

    const match = users.find(
      (u: any) => {
        const userEmail = (u.email || u.email_id || u.user_email || "").toLowerCase().trim();
        return userEmail === user.email_id.toLowerCase().trim();
      }
    );

    if (match && match.id) {
      setDataToSave((prev: any) => ({
        ...prev,
        user_id: match.id,
      }));
    }
  }, [user, foreignKeyData]);

  const fetchForeignData = async (
    foreignResource: string,
    fieldName: string,
    foreignField: string
  ) => {
    try {
      const data = await fetchForeignResource(foreignResource);
      setForeignKeyData((prev: any) => ({
        ...prev,
        [foreignResource]: data,
      }));
    } catch (err) {
      console.error(`Error fetching foreign data for ${fieldName}:`, err);
    }
  };

  const fetchEnumData = async (enumName: string) => {
    try {
      const data = await fetchEnum(enumName);
      setEnums((prev: any) => ({
        ...prev,
        [enumName]: data,
      }));
    } catch (err) {
      console.error(`Error fetching enum data for ${enumName}:`, err);
    }
  };

  const { data: metaData, isLoading, error } = useQuery({
    queryKey: ["resMetaData", "guardianCreate"],
    queryFn: async () => {
      const res = await fetch(metadataUrl, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch metadata: ${res.statusText}`);
      }

      const data = await res.json();

      setResMetaData(data);
      setFields(data[0].fieldValues);

      const foreignFields = data[0].fieldValues.filter(
        (field: any) => field.foreign
      );
      for (const field of foreignFields) {
        if (!fetchedResources.current.has(field.foreign)) {
          fetchedResources.current.add(field.foreign);

          queryClient.prefetchQuery({
            queryKey: ["foreignData", field.foreign],
            queryFn: () => fetchForeignResource(field.foreign),
          });

          await fetchForeignData(
            field.foreign,
            field.name,
            field.foreign_field
          );
        }
      }

      const enumFields = data[0].fieldValues.filter(
        (field: any) => field.isEnum === true
      );
      for (const field of enumFields) {
        if (!fetchedEnum.current.has(field.possible_value)) {
          fetchedEnum.current.add(field.possible_value);

          queryClient.prefetchQuery({
            queryKey: ["enum", field.possible_value],
            queryFn: () => fetchEnum(field.possible_value),
          });

          await fetchEnumData(field.possible_value);
        }
      }

      return data;
    },
  });

  useEffect(() => {
    console.log("data to save", dataToSave);
  }, [dataToSave]);

  const { data: dataRes, isLoading: isLoadingDataRes, error: errorDataRes } = useQuery({
    queryKey: ["resourceData", "guardianCreate", user?.userId, localStorage.getItem("activeDriveId")],
    queryFn: async () => {
      if (!user?.userId) return [];
      const response = await apiFetch(
        `${apiConfig.getResourceUrl('applications')}?queryId=GET_APPLICATIONS_BY_USER_ID&args=user_id:${user.userId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error("Error: " + response.status);
      }

      const data = await response.json();
      return data.resource || [];
    },
    enabled: !!user?.userId
  });

  useEffect(() => {
    if (dataRes) {
      let dataToStore = { resource: "guardian", records: dataRes };
      useRaspStore.getState().initializeStore(user?.userId || "", dataToStore);
    }
  }, [dataRes, user]);

  // Pre-populate form with existing data when user_id is resolved and records are loaded
  useEffect(() => {
    const activeDriveId = localStorage.getItem("activeDriveId");
    if (!hasPrePopulated.current && dataRes && dataRes.length > 0 && dataToSave.user_id && activeDriveId) {
      const userRecord = dataRes.find((r: any) => r.user_id === dataToSave.user_id && r.drive_id === activeDriveId);
      if (userRecord) {
        hasPrePopulated.current = true;
        // Merge userRecord into dataToSave, ensuring id and drive_id are preserved
        setDataToSave((prev: any) => ({ ...userRecord, ...prev }));
      }
    }
  }, [dataRes, dataToSave.user_id])

  const handleCreate = async () => {
    const vErrors = validate(dataToSave, guardianValidationRules);
    setErrors(vErrors);
    
    await save();
    queryClient.invalidateQueries({ queryKey: ['resourceData', 'guardianCreate'] });
  };

  useImperativeHandle(ref, () => ({
    save: handleCreate,
    getErrors: () => errors
  }));

  const handleSearchChange = (fieldName: string, value: string) => {
    setSearchQueries((prev) => ({ ...prev, [fieldName]: value }));
  };

  return (
    <div className="form-container">
      <h3 className="form-subtitle">Parent / Guardian Details</h3>
      <p className="form-description" style={{ color: "#666", fontSize: "0.9rem", marginBottom: "20px", marginTop: "-10px" }}>
        Note: Registered Parent/Guardian will be allowed to sign/attest during the admissions process as needed.
      </p>

      <div className="form-grid">
        {/* Row 1 */}
        <div className="form-group">
          <label className="form-label">Name *</label>
          <input
            type="text"
            className={`form-input ${errors.guardian_name ? "input-error" : ""}`}
            name="guardian_name"
            required
            value={dataToSave["guardian_name"] || ""}
            placeholder="Full Name"
            onChange={(e) => {
              setDataToSave({ ...dataToSave, guardian_name: e.target.value });
              if (errors.guardian_name) setErrors(prev => ({ ...prev, guardian_name: "" }));
            }}
          />
          {errors.guardian_name && <span className="error-text">{errors.guardian_name}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Relation *</label>
          <select
            className={`form-select ${errors.guardian_relation ? "input-error" : ""}`}
            name="guardian_relation"
            required
            value={["Father", "Mother", ""].includes(dataToSave["guardian_relation"] || "") ? (dataToSave["guardian_relation"] || "") : "Other"}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "Other") {
                setDataToSave({ ...dataToSave, guardian_relation: " " });
              } else {
                setDataToSave({ ...dataToSave, guardian_relation: val });
              }
              if (errors.guardian_relation) setErrors(prev => ({ ...prev, guardian_relation: "" }));
            }}
          >
            <option value="">Select Relation</option>
            <option value="Father">Father</option>
            <option value="Mother">Mother</option>
            <option value="Other">Other</option>
          </select>
          {errors.guardian_relation && <span className="error-text">{errors.guardian_relation}</span>}
        </div>
        {!["Father", "Mother", ""].includes(dataToSave["guardian_relation"] || "") && (
          <div className="form-group">
            <label className="form-label">Relation *</label>
            <input
              type="text"
              className={`form-input ${errors.guardian_relation ? "input-error" : ""}`}
              required
              value={dataToSave["guardian_relation"]?.trim() || ""}
              placeholder="e.g. Brother, Uncle"
              onChange={(e) => {
                setDataToSave({ ...dataToSave, guardian_relation: e.target.value || " " });
                if (errors.guardian_relation) setErrors(prev => ({ ...prev, guardian_relation: "" }));
              }}
            />
            {errors.guardian_relation && <span className="error-text">{errors.guardian_relation}</span>}
          </div>
        )}

        {/* Row 2 */}
        <div className="form-group">
          <label className="form-label">Mobile Number *</label>
          <input
            type="text"
            className={`form-input ${errors.guardian_mobile ? "input-error" : ""}`}
            name="guardian_mobile"
            required
            maxLength={10}
            value={dataToSave["guardian_mobile"] || ""}
            placeholder="Mobile Number"
            style={{ borderColor: (mobileError || errors.guardian_mobile) ? "red" : "" }}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, ""); // Only allow digits
              setDataToSave({ ...dataToSave, guardian_mobile: val });

              if (errors.guardian_mobile) setErrors(prev => ({ ...prev, guardian_mobile: "" }));

              if (val && val.length !== 10) {
                setMobileError("Mobile number must be exactly 10 digits.");
              } else {
                setMobileError("");
              }
            }}
          />
          {(mobileError || errors.guardian_mobile) && (
            <span style={{ color: "red", fontSize: "0.8rem", marginTop: "4px" }}>
              {mobileError || errors.guardian_mobile}
            </span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Email *</label>
          <input
            type="email"
            className={`form-input ${errors.guardian_email ? "input-error" : ""}`}
            name="guardian_email"
            required
            value={dataToSave["guardian_email"] || ""}
            placeholder="Email Address"
            onChange={(e) => {
              setDataToSave({ ...dataToSave, guardian_email: e.target.value });
              if (errors.guardian_email) setErrors(prev => ({ ...prev, guardian_email: "" }));
            }}
          />
          {errors.guardian_email && <span className="error-text">{errors.guardian_email}</span>}
        </div>

        {/* Row 3 */}
        <div className="form-group">
          <label className="form-label">Alternate Mobile</label>
          <input
            type="text"
            className="form-input"
            name="alternatemobile"
            maxLength={10}
            value={dataToSave["alternatemobile"] || ""}
            placeholder="Alternate Mobile Number"
            style={{ borderColor: altMobileError ? "red" : "" }}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, ""); // Only allow digits
              setDataToSave({ ...dataToSave, alternatemobile: val });

              if (val && val.length !== 10) {
                setAltMobileError("Alternate mobile number must be exactly 10 digits.");
              } else {
                setAltMobileError("");
              }
            }}
          />
          {altMobileError && <span style={{ color: "red", fontSize: "0.8rem", marginTop: "4px" }}>{altMobileError}</span>}
        </div>
      </div>

    </div>
  );
});

export default CreateGuardian;
